import { fetchWithSupabaseAuth } from "../../lib/supabaseClient.js";

export const PULL_FORWARD_CANDIDATES_API_URL = "/api/planning/pull-forward/candidates";
export const PULL_FORWARD_ANALYSIS_API_URL = "/api/planning/pull-forward/analysis";
export const PULL_FORWARD_PREVIEW_API_URL = "/api/planning/pull-forward/preview";
export const PULL_FORWARD_APPLY_API_URL = "/api/planning/pull-forward/apply";
export const PULL_FORWARD_REQUEST_TIMEOUT_MS = 29_000;
export const PULL_FORWARD_MUTATION_TIMEOUT_MS = 125_000;

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizePullForwardDispatchPeriod(value) {
  const normalizedValue = normalizeText(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

export function buildPullForwardCandidatesUrl(dispatchDate, dispatchPeriod = "day") {
  const query = new URLSearchParams({
    dispatch_date: normalizeText(dispatchDate),
    dispatch_period: normalizePullForwardDispatchPeriod(dispatchPeriod),
  });
  return `${PULL_FORWARD_CANDIDATES_API_URL}?${query.toString()}`;
}

export function buildPullForwardAnalysisUrl(dispatchDate, dispatchPeriod = "day") {
  const query = new URLSearchParams({
    dispatch_date: normalizeText(dispatchDate),
    dispatch_period: normalizePullForwardDispatchPeriod(dispatchPeriod),
  });
  return `${PULL_FORWARD_ANALYSIS_API_URL}?${query.toString()}`;
}

export function buildPullForwardAnalysisStatusUrl(jobId) {
  return `${PULL_FORWARD_ANALYSIS_API_URL}/${encodeURIComponent(normalizeText(jobId))}`;
}

export function buildPullForwardAnalysisCancelUrl(jobId) {
  return `${buildPullForwardAnalysisStatusUrl(jobId)}/cancel`;
}

export function buildPullForwardSelections(selectionByBlock = {}) {
  const entries = selectionByBlock instanceof Map
    ? [...selectionByBlock.entries()]
    : Object.entries(selectionByBlock && typeof selectionByBlock === "object" ? selectionByBlock : {});

  return entries
    .map(([blockId, target]) => ({
      block_id: normalizeText(blockId),
      target_truck_id: normalizeText(target?.targetTruckId || target?.target_truck_id),
      target_trip_id: normalizeText(target?.targetTripId || target?.target_trip_id),
    }))
    .filter((selection) => (
      selection.block_id && selection.target_truck_id && selection.target_trip_id
    ))
    .sort((left, right) => left.block_id.localeCompare(right.block_id));
}

function resolveErrorMessage(payload, fallbackMessage) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }
  if (typeof payload?.detail?.message === "string" && payload.detail.message.trim()) {
    return payload.detail.message.trim();
  }
  if (typeof payload?.detail?.error === "string" && payload.detail.error.trim()) {
    return payload.detail.error.trim();
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }
  return fallbackMessage;
}

export class PullForwardApiError extends Error {
  constructor(message, { code = "", payload = null, status = 0 } = {}) {
    super(message);
    this.name = "PullForwardApiError";
    this.code = normalizeText(code);
    this.payload = payload;
    this.status = Number(status) || 0;
  }
}

export function composePullForwardRequestSignal(
  externalSignal,
  timeoutMs = PULL_FORWARD_REQUEST_TIMEOUT_MS,
) {
  let timeoutSignal = null;
  let fallbackTimeoutController = null;
  let fallbackTimeoutId = null;

  if (
    typeof AbortSignal === "function"
    && typeof AbortSignal.timeout === "function"
  ) {
    timeoutSignal = AbortSignal.timeout(timeoutMs);
  } else if (typeof AbortController === "function") {
    fallbackTimeoutController = new AbortController();
    timeoutSignal = fallbackTimeoutController.signal;
    fallbackTimeoutId = globalThis.setTimeout(() => {
      fallbackTimeoutController.abort();
    }, timeoutMs);
  }

  const cleanupTimeout = () => {
    if (fallbackTimeoutId != null) {
      globalThis.clearTimeout(fallbackTimeoutId);
      fallbackTimeoutId = null;
    }
  };
  const didTimeout = () => Boolean(
    timeoutSignal?.aborted
    && !externalSignal?.aborted,
  );

  if (!externalSignal || !timeoutSignal) {
    return {
      signal: externalSignal || timeoutSignal,
      didTimeout,
      cleanup: cleanupTimeout,
    };
  }

  if (
    typeof AbortSignal === "function"
    && typeof AbortSignal.any === "function"
  ) {
    return {
      signal: AbortSignal.any([externalSignal, timeoutSignal]),
      didTimeout,
      cleanup: cleanupTimeout,
    };
  }

  if (typeof AbortController !== "function") {
    return {
      signal: externalSignal,
      didTimeout,
      cleanup: cleanupTimeout,
    };
  }

  const composedController = new AbortController();
  const abortFromExternal = () => composedController.abort(externalSignal.reason);
  const abortFromTimeout = () => composedController.abort(timeoutSignal.reason);
  if (externalSignal.aborted) {
    abortFromExternal();
  } else {
    externalSignal.addEventListener("abort", abortFromExternal, { once: true });
  }
  if (timeoutSignal.aborted) {
    abortFromTimeout();
  } else {
    timeoutSignal.addEventListener("abort", abortFromTimeout, { once: true });
  }

  return {
    signal: composedController.signal,
    didTimeout,
    cleanup: () => {
      cleanupTimeout();
      externalSignal.removeEventListener("abort", abortFromExternal);
      timeoutSignal.removeEventListener("abort", abortFromTimeout);
    },
  };
}

function timeoutErrorMessage(fallbackMessage, timeoutMs) {
  const timeoutSeconds = Math.max(1, Math.round(Number(timeoutMs) / 1000));
  return `${fallbackMessage} La limite de ${timeoutSeconds} secondes a été atteinte.`;
}

async function requestJson(
  url,
  init,
  fallbackMessage,
  timeoutMs = PULL_FORWARD_REQUEST_TIMEOUT_MS,
) {
  const requestInit = { ...(init || {}) };
  const externalSignal = requestInit.signal || null;
  const requestSignal = composePullForwardRequestSignal(externalSignal, timeoutMs);
  if (requestSignal.signal) {
    requestInit.signal = requestSignal.signal;
  }
  let response;
  try {
    response = await fetchWithSupabaseAuth(url, requestInit);
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new PullForwardApiError(resolveErrorMessage(payload, fallbackMessage), {
        code: payload?.code || payload?.detail?.code || payload?.detail?.reason_code,
        payload,
        status: response.status,
      });
    }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      throw new PullForwardApiError("Le serveur a retourné une réponse incomplète.", {
        payload,
        status: response.status,
      });
    }
    return payload;
  } catch (error) {
    // An explicit controller is used by the analysis poller. Cancellation is
    // expected there and must not become a user-facing timeout/error.
    if (externalSignal?.aborted) {
      throw error;
    }
    if (
      requestSignal.didTimeout()
      || error?.name === "AbortError"
      || error?.name === "TimeoutError"
    ) {
      throw new PullForwardApiError(timeoutErrorMessage(fallbackMessage, timeoutMs), {
        code: "pull_forward_client_timeout",
      });
    }
    throw error;
  } finally {
    requestSignal.cleanup();
  }
}

export async function fetchPullForwardCandidates({ dispatchDate, dispatchPeriod }) {
  return requestJson(
    buildPullForwardCandidatesUrl(dispatchDate, dispatchPeriod),
    undefined,
    "Impossible d’analyser les commandes de J+1 et J+2.",
  );
}

export async function startPullForwardAnalysis({ dispatchDate, dispatchPeriod, signal }) {
  return requestJson(
    PULL_FORWARD_ANALYSIS_API_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        dispatch_date: normalizeText(dispatchDate),
        dispatch_period: normalizePullForwardDispatchPeriod(dispatchPeriod),
      }),
      signal,
    },
    "Impossible de lancer l’analyse des commandes de J+1 et J+2.",
  );
}

export async function fetchPullForwardAnalysis(jobId, { signal } = {}) {
  return requestJson(
    buildPullForwardAnalysisStatusUrl(jobId),
    { signal },
    "Impossible de suivre l’analyse des commandes de J+1 et J+2.",
  );
}

export async function cancelPullForwardAnalysis(jobId, { signal } = {}) {
  return requestJson(
    buildPullForwardAnalysisCancelUrl(jobId),
    { method: "POST", signal },
    "Impossible d’annuler l’analyse des commandes de J+1 et J+2.",
  );
}

export async function previewPullForward(payload) {
  return requestJson(PULL_FORWARD_PREVIEW_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de prévisualiser le regroupement.", PULL_FORWARD_MUTATION_TIMEOUT_MS);
}

export async function applyPullForward(payload) {
  return requestJson(PULL_FORWARD_APPLY_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d’appliquer le regroupement.", PULL_FORWARD_MUTATION_TIMEOUT_MS);
}

export async function fetchLatestPullForwardResults({ dispatchDate, dispatchPeriod }) {
  const query = new URLSearchParams({
    dispatch_date: normalizeText(dispatchDate),
    dispatch_period: normalizePullForwardDispatchPeriod(dispatchPeriod),
  });
  return requestJson(
    `/api/results/latest?${query.toString()}`,
    undefined,
    "Le regroupement a été appliqué, mais le dispatch n’a pas pu être rafraîchi.",
  );
}
